import { inject, Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import Prism from 'prismjs';
import { MENTION_PATTERN, mentionChip } from './mention-chip';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-yaml';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-sql';

/**
 * Converts markdown string (with GFM support) into sanitized HTML.
 *
 * Supported syntax:
 *   - Headers            # / ## / ### / etc
 *   - Bold               **...**
 *   - Italic             *...*
 *   - Bold & Italic      ***...***
 *   - Inline code        `...`
 *   - Code blocks        ```lang\n...\n``` (with syntax highlighting)
 *   - Mermaid diagrams   ```mermaid\n...\n```
 *   - Plotly charts      ```plotly\n...\n```
 *   - Data tables        ```datatable\n...\n```
 *   - Tables             | header | (GFM style)
 *   - Lists              - item or 1. item
 *   - Blockquotes        > text
 *   - Links              [text](url) and [email](mailto:email)
 *   - Reference mentions @[label](indicator:code) / @[label](country:ISO) / @[label](region:code)
 *   - Horizontal rules   ---
 *   - Line breaks        \n\n for paragraphs
 *
 * NOTE: Content comes from the application's own backend (authenticated).
 * Mermaid blocks are kept as `<div data-mermaid>` for later initialization.
 */
@Pipe({ name: 'markdown', standalone: true })
export class MarkdownPipe implements PipeTransform {
  private sanitizer = inject(DomSanitizer);
  private configurationDone = false;

  transform(value: string): SafeHtml {
    if (!value) return '';

    // Configure marked only once
    if (!this.configurationDone) {
      this.configureMarked();
      this.configurationDone = true;
    }

    try {
      const html = marked.parse(this.renderMentions(value)) as string;
      return this.sanitizer.bypassSecurityTrustHtml(html);
    } catch (error) {
      console.error('Markdown parsing error:', error);
      return this.sanitizer.bypassSecurityTrustHtml(this.escapeHtml(value));
    }
  }

  /**
   * Turns `@[label](indicator:code)` into a chip before markdown is parsed.
   *
   * Done here rather than in the `link` renderer on purpose: indicator codes contain
   * sequences such as `%_` that are not valid percent-encoding, and marked's URL
   * cleaning would mangle or drop them. Emitting the span up front keeps the code out
   * of marked's URL handling entirely; both label and code are escaped, and the result
   * flows through as inline HTML.
   */
  private renderMentions(value: string): string {
    // Only the two captured fields are escaped here — the rest of the string is markdown
    // and must reach `marked` untouched.
    return value.replace(MENTION_PATTERN, (_match, label: string, kind: string, code: string) =>
      mentionChip(`@${this.escapeHtml(label)}`, kind, this.escapeHtml(code))
    );
  }

  private configureMarked(): void {
    // Save a reference to this pipe so regular functions below can access escapeHtml.
    // We MUST use regular function declarations (not arrow functions) so that
    // marked.use() can bind `this` to the renderer instance, giving us access
    // to `this.parser.parseInline()` / `this.parser.parse()` for rendering
    // inline tokens (bold, italic, code, links, etc.) inside block elements.
    const escapeHtml = this.escapeHtml.bind(this);

    const renderer = {
      // ── Code block ─────────────────────────────────────────────────────────
      // Intercept mermaid fences and apply prism for everything else.
      code(this: any, token: any): string {
        const { text, lang } = token;

        if (lang === 'mermaid') {
          // Store raw code in a data attribute (no escaping needed, encodeURIComponent
          // handles all special characters safely for an HTML attribute value).
          return `<div class="mermaid-diagram" data-mermaid-src="${encodeURIComponent(text)}"></div>`;
        }

        if (lang === 'datatable') {
          // Skeleton shown until DataTableService replaces it with the rendered table.
          const skeleton = `<div class="datatable-skeleton"><div class="datatable-skeleton-spinner"></div><span>Loading data...</span></div>`;
          return `<div class="data-table" data-datatable-src="${encodeURIComponent(text)}">${skeleton}</div>`;
        }

        if (lang === 'plotly') {
          // Skeleton shown until PlotlyService replaces it with the rendered chart.
          const skeleton = `<div class="plotly-skeleton"><div class="plotly-skeleton-spinner"></div><span>Generating chart...</span></div>`;
          return `<div class="plotly-diagram" data-plotly-src="${encodeURIComponent(text)}">${skeleton}</div>`;
        }

        let highlighted = text;
        if (lang && Prism.languages[lang]) {
          try {
            highlighted = Prism.highlight(text, Prism.languages[lang], lang);
          } catch {
            highlighted = escapeHtml(text);
          }
        } else {
          highlighted = escapeHtml(text);
        }

        const cls = lang ? ` language-${lang}` : '';
        return `<pre class="code-block"><code class="code-content${cls}">${highlighted}</code></pre>`;
      },

      // ── Table ──────────────────────────────────────────────────────────────
      // In marked v5+, `token.header` and `token.rows` contain TableCell objects
      // (not strings). Each cell has `.tokens` (inline token array) that must be
      // run through `this.parser.parseInline()` to produce rendered HTML.
      table(this: any, token: any): string {
        let html = '<table class="markdown-table">\n<thead><tr>\n';

        for (let i = 0; i < token.header.length; i++) {
          const cell = token.header[i];
          const align = cell.align ? ` class="text-${cell.align}"` : '';
          const content = this.parser?.parseInline(cell.tokens) ?? escapeHtml(cell.text);
          html += `<th${align}>${content}</th>\n`;
        }
        html += '</tr></thead>\n<tbody>\n';

        for (const row of token.rows) {
          html += '<tr>\n';
          for (let i = 0; i < row.length; i++) {
            const cell = row[i];
            const align = cell.align ? ` class="text-${cell.align}"` : '';
            const content = this.parser?.parseInline(cell.tokens) ?? escapeHtml(cell.text);
            html += `<td${align}>${content}</td>\n`;
          }
          html += '</tr>\n';
        }

        return html + '</tbody>\n</table>';
      },

      // ── Blockquote ─────────────────────────────────────────────────────────
      // token.tokens are block-level tokens (paragraphs etc.) inside the quote.
      blockquote(this: any, token: any): string {
        const content = this.parser?.parse(token.tokens) ?? escapeHtml(token.text);
        return `<blockquote class="markdown-blockquote">${content}</blockquote>`;
      },

      // ── Heading ────────────────────────────────────────────────────────────
      heading(this: any, token: any): string {
        const content = this.parser?.parseInline(token.tokens) ?? escapeHtml(token.text);
        return `<h${token.depth} class="markdown-heading">${content}</h${token.depth}>`;
      },

      // ── Link ───────────────────────────────────────────────────────────────
      link(this: any, token: any): string {
        const isEmail = token.href.startsWith('mailto:');
        const title = token.title ? ` title="${token.title}"` : '';
        const target = !isEmail ? ' target="_blank" rel="noopener noreferrer"' : '';
        const content = this.parser?.parseInline(token.tokens) ?? token.text;
        return `<a href="${token.href}"${title}${target}>${content}</a>`;
      },

      // ── Horizontal rule ────────────────────────────────────────────────────
      hr(): string {
        return '<hr class="markdown-hr">';
      },

      // ── Inline code ────────────────────────────────────────────────────────
      codespan(this: any, token: any): string {
        return `<code class="inline-code">${escapeHtml(token.text)}</code>`;
      },
    };

    marked.use({
      gfm: true,
      breaks: true,
      pedantic: false,
      renderer,
    });
  }

  private escapeHtml(text: string): string {
    const map: { [key: string]: string } = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };
    return text.replace(/[&<>"']/g, (char) => map[char]);
  }
}
